import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  MapPin, 
  Maximize2, 
  ArrowLeft, 
  ArrowRight,
  Phone, 
  Mail, 
  Calendar,
  CheckCircle,
  Home,
  TreePine,
  Car,
  Droplets,
  Zap,
  ChevronRight,
  Share2,
  Heart,
  Hammer,
  Ruler,
  Building2,
  Shield,
  FileText,
  Layers
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { constructionProjects } from '../mockData';
import AnimatedSection from '../components/AnimatedSection';
import MapView from '../components/MapView';

const ConstructionProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isGalleryHovered, setIsGalleryHovered] = useState(false);

  // Убеждаемся, что activeImageIndex не выходит за границы массива
  useEffect(() => {
    if (project && project.gallery && Array.isArray(project.gallery) && project.gallery.length > 0) {
      if (activeImageIndex >= project.gallery.length) {
        setActiveImageIndex(0);
      }
    }
  }, [project, activeImageIndex]);

  useEffect(() => {
    if (
      !project ||
      !project.gallery ||
      !Array.isArray(project.gallery) ||
      project.gallery.length <= 1 ||
      isGalleryHovered
    ) {
      return;
    }

    const interval = setInterval(() => {
      setActiveImageIndex((prevIndex) => {
        const total = project.gallery.length;
        return total > 0 ? (prevIndex + 1) % total : 0;
      });
    }, 4000);

    return () => clearInterval(interval);
  }, [project, isGalleryHovered]);

  useEffect(() => {
    const foundProject = constructionProjects.find(p => p && p.id === id);
    if (foundProject) {
      setProject(foundProject);
      // Сбрасываем индекс изображения при смене проекта
      setActiveImageIndex(0);
    }
  }, [id]);

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Projet introuvable</h1>
          <Button onClick={() => navigate('/')} className="bg-[#c4d600] hover:bg-[#b0c200] text-gray-800">
            Retour à l'accueil
          </Button>
        </div>
      </div>
    );
  }

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: project.title,
          text: `Découvrez ce projet de construction à ${project.location}`,
          url: window.location.href
        });
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Lien copié dans le presse-papiers !');
    }
  };

  const handlePrevImage = () => {
    if (!project || !project.gallery || project.gallery.length === 0) return;
    setActiveImageIndex((prevIndex) => {
      const total = project.gallery.length;
      if (total === 0) return 0;
      return (prevIndex - 1 + total) % total;
    });
  };

  const handleNextImage = () => {
    if (!project || !project.gallery || project.gallery.length === 0) return;
    setActiveImageIndex((prevIndex) => {
      const total = project.gallery.length;
      if (total === 0) return 0;
      return (prevIndex + 1) % total;
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb */}
      <AnimatedSection animation="fade-in">
        <div className="bg-gray-50 py-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center text-sm text-gray-600">
              <Link to="/" className="hover:text-[#c4d600] transition-colors">Accueil</Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span className="text-gray-800 font-medium">{project.title}</span>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Hero Section with Image Gallery */}
      <AnimatedSection animation="fade-up">
        <div className="relative">
          <div
            className="relative h-[60vh] md:h-[70vh] overflow-hidden bg-gray-200"
            onMouseEnter={() => setIsGalleryHovered(true)}
            onMouseLeave={() => setIsGalleryHovered(false)}
          >
            <img
              src={project.gallery && project.gallery[activeImageIndex] ? project.gallery[activeImageIndex] : project.image}
              alt={project.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/1200x800?text=Image+non+disponible';
                e.target.onerror = null;
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
            
            {/* Arrow Navigation */}
            {project.gallery && Array.isArray(project.gallery) && project.gallery.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={handlePrevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/20 backdrop-blur-md text-white hover:bg-white/30 transition-all z-10"
                  aria-label="Image précédente"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={handleNextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/20 backdrop-blur-md text-white hover:bg-white/30 transition-all z-10"
                  aria-label="Image suivante"
                >
                  <ArrowRight className="w-5 h-5" />
                </button>
              </>
            )}
            
            {/* Image Navigation */}
            {project.gallery && Array.isArray(project.gallery) && project.gallery.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
                {project.gallery.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveImageIndex(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      activeImageIndex === index ? 'bg-[#c4d600] w-8' : 'bg-white/50 hover:bg-white/80'
                    }`}
                    aria-label={`Voir l'image ${index + 1}`}
                  />
                ))}
              </div>
            )}

            {/* Action Buttons */}
            <div className="absolute top-4 right-4 flex space-x-2 z-10">
              <button
                onClick={() => setIsFavorite(!isFavorite)}
                className={`p-3 rounded-full backdrop-blur-md transition-all ${
                  isFavorite 
                    ? 'bg-[#c4d600] text-gray-800' 
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
                aria-label={isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}
              >
                <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
              </button>
              <button
                onClick={handleShare}
                className="p-3 rounded-full bg-white/20 backdrop-blur-md text-white hover:bg-white/30 transition-all"
                aria-label="Partager"
              >
                <Share2 className="w-5 h-5" />
              </button>
            </div>

            {/* Back Button */}
            <div className="absolute top-4 left-4 z-10">
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                className="bg-white/20 backdrop-blur-md text-white hover:bg-white/30"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
            </div>

            {/* Title Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
              <div className="max-w-7xl mx-auto">
                <h1 className="text-4xl md:text-5xl font-bold mb-2">{project.title}</h1>
                {project.subtitle && (
                  <p className="text-xl md:text-2xl mb-4 opacity-90">{project.subtitle}</p>
                )}
                <div className="flex flex-wrap items-center gap-4 text-lg">
                  <div className="flex items-center">
                    <MapPin className="w-5 h-5 mr-2 text-[#c4d600]" />
                    <span>{project.fullAddress || project.location}</span>
                  </div>
                  {project.altitude && (
                    <div className="flex items-center">
                      <TreePine className="w-5 h-5 mr-2 text-[#c4d600]" />
                      <span>{project.altitude} m d'altitude</span>
                    </div>
                  )}
                  <div className="flex items-center bg-[#c4d600]/20 px-3 py-1 rounded-full">
                    <Hammer className="w-4 h-4 mr-2 text-[#c4d600]" />
                    <span className="text-sm font-semibold">{project.status}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Project Info Bar */}
      <AnimatedSection animation="fade-up" delay={0.1}>
        <div className="bg-[#c4d600] py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div>
                <p className="text-gray-800 text-sm font-semibold mb-1">Type de projet</p>
                <p className="text-2xl font-bold text-gray-800">{project.projectType}</p>
              </div>
              <div className="flex flex-wrap gap-4">
                {project.surfaces && (
                  <>
                    <div className="text-center">
                      <p className="text-gray-800 text-sm font-semibold">Surface totale</p>
                      <p className="text-2xl font-bold text-gray-800">{project.surfaces.totale} m²</p>
                    </div>
                    <div className="text-center">
                      <p className="text-gray-800 text-sm font-semibold">Surface utilisable</p>
                      <p className="text-2xl font-bold text-gray-800">{project.surfaces.utilisable} m²</p>
                    </div>
                  </>
                )}
                <div className="text-center">
                  <p className="text-gray-800 text-sm font-semibold">Localisation</p>
                  <p className="text-lg font-bold text-gray-800">{project.commune || project.location}</p>
                  {project.department && (
                    <p className="text-sm text-gray-700">{project.department}</p>
                  )}
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  asChild
                  className="bg-gray-800 hover:bg-gray-900 text-white"
                >
                  <a href={`tel:0492000000`}>
                    <Phone className="w-4 h-4 mr-2" />
                    Appeler
                  </a>
                </Button>
                <Button
                  asChild
                  className="bg-gray-800 hover:bg-gray-900 text-white"
                >
                  <a href={`mailto:Contact@artduchalet.com?subject=Demande d'information - ${project.title}`}>
                    <Mail className="w-4 h-4 mr-2" />
                    Contacter
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <AnimatedSection animation="fade-up">
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">Description du projet</h2>
                {project.longDescription ? (
                  <div className="text-gray-600 text-lg leading-relaxed whitespace-pre-line">
                    {project.longDescription.split('\n\n').filter(p => p && p.trim()).map((paragraph, index) => (
                      <p key={index} className={paragraph.trim().startsWith('**') ? 'font-bold text-gray-800 mt-4 mb-2' : 'mb-4'}>
                        {paragraph.trim().replace(/\*\*/g, '')}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-600 text-lg leading-relaxed">{project.description || 'Aucune description disponible.'}</p>
                )}
              </div>
            </AnimatedSection>

            {/* Surfaces */}
            {project.surfaces && (
              <AnimatedSection animation="fade-up" delay={0.1}>
                <div>
                  <h2 className="text-3xl font-bold text-gray-800 mb-6">Surfaces</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4 hover:bg-[#c4d600]/10 transition-colors">
                      <Maximize2 className="w-6 h-6 text-[#c4d600] mb-2" />
                      <p className="text-sm text-gray-600 mb-1">Surface au sol</p>
                      <p className="font-semibold text-gray-800">{project.surfaces.sol} m²</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 hover:bg-[#c4d600]/10 transition-colors">
                      <Home className="w-6 h-6 text-[#c4d600] mb-2" />
                      <p className="text-sm text-gray-600 mb-1">Surface utilisable</p>
                      <p className="font-semibold text-gray-800">{project.surfaces.utilisable} m²</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 hover:bg-[#c4d600]/10 transition-colors">
                      <Building2 className="w-6 h-6 text-[#c4d600] mb-2" />
                      <p className="text-sm text-gray-600 mb-1">Surface totale</p>
                      <p className="font-semibold text-gray-800">{project.surfaces.totale} m²</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 hover:bg-[#c4d600]/10 transition-colors">
                      <Ruler className="w-6 h-6 text-[#c4d600] mb-2" />
                      <p className="text-sm text-gray-600 mb-1">Zone de construction</p>
                      <p className="font-semibold text-gray-800">{project.surfaces.construction} m²</p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            )}

            {/* Floor Plans */}
            {project.floors && Object.keys(project.floors).length > 0 && (
              <AnimatedSection animation="fade-up" delay={0.2}>
                <div>
                  <h2 className="text-3xl font-bold text-gray-800 mb-6">Plans des niveaux</h2>
                  <div className="space-y-6">
                    {Object.entries(project.floors).map(([key, floor]) => (
                      floor && floor.rooms && Array.isArray(floor.rooms) && (
                        <div key={key} className="bg-gray-50 rounded-lg p-6">
                          <h3 className="text-xl font-bold text-gray-800 mb-4">{floor.name || `Niveau ${key}`}</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {floor.rooms.map((room, index) => (
                              room && (
                                <div key={index} className="flex justify-between items-center py-2 border-b border-gray-200 last:border-0">
                                  <span className="text-gray-700">{room.name || 'Pièce'}</span>
                                  {room.surface && (
                                    <span className="font-semibold text-gray-800">{room.surface} m²</span>
                                  )}
                                </div>
                              )
                            ))}
                          </div>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              </AnimatedSection>
            )}

            {/* Technical Specifications */}
            {project.technicalSpecs && (
              <AnimatedSection animation="fade-up" delay={0.3}>
                <div>
                  <h2 className="text-3xl font-bold text-gray-800 mb-6">Caractéristiques techniques</h2>
                  <div className="bg-gray-50 rounded-lg p-6">
                    <div className="space-y-4">
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Structure</span>
                        <span className="font-semibold text-gray-800 text-right max-w-md">{project.technicalSpecs.structure}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Isolation murs extérieurs</span>
                        <span className="font-semibold text-gray-800 text-right max-w-md">{project.technicalSpecs.isolationMurs}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Isolation toiture</span>
                        <span className="font-semibold text-gray-800 text-right max-w-md">{project.technicalSpecs.isolationToiture}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Pare-vapeur</span>
                        <span className="font-semibold text-gray-800 text-right max-w-md">{project.technicalSpecs.pareVapeur}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Protection vent/humidité</span>
                        <span className="font-semibold text-gray-800 text-right max-w-md">{project.technicalSpecs.protectionVentHumidite}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Contreventement</span>
                        <span className="font-semibold text-gray-800 text-right max-w-md">{project.technicalSpecs.contreventement}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-600">Poutre faîtière</span>
                        <span className="font-semibold text-gray-800 text-right max-w-md">{project.technicalSpecs.poutreFaitiere}</span>
                      </div>
                      <div className="flex justify-between py-2">
                        <span className="text-gray-600">Garantie constructeur</span>
                        <span className="font-semibold text-gray-800 text-right max-w-md">{project.technicalSpecs.garantie}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            )}

            {/* Nearby Points */}
            {project.nearbyPoints && Array.isArray(project.nearbyPoints) && project.nearbyPoints.length > 0 && (
              <AnimatedSection animation="fade-up" delay={0.4}>
                <div>
                  <h2 className="text-3xl font-bold text-gray-800 mb-6">À proximité</h2>
                  <div className="bg-gradient-to-r from-[#c4d600]/10 to-transparent rounded-lg p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {project.nearbyPoints.map((point, index) => (
                        point && point.name && (
                          <div key={index} className="flex items-center text-gray-700">
                            <CheckCircle className="w-5 h-5 text-[#c4d600] mr-3 flex-shrink-0" />
                            <div>
                              <span className="font-semibold">{point.name}</span>
                              {point.distance && (
                                <span className="text-gray-600 ml-2">({point.distance})</span>
                              )}
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            )}

            {/* Map */}
            {project.coordinates && (
              <AnimatedSection animation="fade-up" delay={0.5}>
                <div>
                  <h2 className="text-3xl font-bold text-gray-800 mb-6">Localisation</h2>
                  <div className="rounded-lg overflow-hidden shadow-lg h-96">
                    <MapView terrains={[{
                      id: project.id,
                      title: project.title,
                      location: project.commune || project.location,
                      coordinates: project.coordinates,
                      image: project.image,
                      status: project.status,
                      price: project.price || 0
                    }]} />
                  </div>
                  <div className="mt-4 flex items-center text-gray-600">
                    <MapPin className="w-5 h-5 mr-2 text-[#c4d600]" />
                    <span>
                      {project.fullAddress || project.location}
                      {project.coordinates && ` - Coordonnées: ${project.coordinates.lat}° N, ${project.coordinates.lng}° E`}
                    </span>
                  </div>
                </div>
              </AnimatedSection>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Card */}
            <AnimatedSection animation="slide-left">
              <div className="bg-gray-50 rounded-lg p-6 sticky top-24">
                <h3 className="text-2xl font-bold text-gray-800 mb-4">Intéressé par ce projet ?</h3>
                <p className="text-gray-600 mb-6">
                  Contactez-nous pour plus d'informations sur ce projet de construction.
                </p>
                <div className="space-y-3">
                  <Button
                    asChild
                    className="w-full bg-[#c4d600] hover:bg-[#b0c200] text-gray-800 font-semibold"
                  >
                    <a href={`mailto:Contact@artduchalet.com?subject=Demande d'information - ${project.title}`}>
                      <Mail className="w-4 h-4 mr-2" />
                      Envoyer un message
                    </a>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="w-full"
                  >
                    <a href={`tel:0492000000`}>
                      <Phone className="w-4 h-4 mr-2" />
                      Appeler maintenant
                    </a>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="w-full"
                  >
                    <Link to="/contact">
                      <Calendar className="w-4 h-4 mr-2" />
                      Prendre rendez-vous
                    </Link>
                  </Button>
                </div>
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-sm text-gray-600 mb-2">Référence projet</p>
                  {project.pdfFile ? (
                    <a
                      href={project.pdfFile}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-lg font-bold text-[#c4d600] hover:text-[#b0c200] hover:underline transition-colors inline-flex items-center gap-2 group"
                      title="Télécharger le PDF du projet"
                    >
                      <FileText className="w-5 h-5 group-hover:scale-110 transition-transform" />
                      <span>#{project.id.toUpperCase()}</span>
                    </a>
                  ) : (
                    <p className="font-mono text-lg font-bold text-gray-800">#{project.id.toUpperCase()}</p>
                  )}
                </div>
              </div>
            </AnimatedSection>

            {/* Quick Stats */}
            <AnimatedSection animation="slide-left" delay={0.1}>
              <div className="bg-gradient-to-br from-[#c4d600] to-[#b0c200] rounded-lg p-6 text-white">
                <h3 className="text-xl font-bold mb-4">Résumé</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="opacity-90">Type</span>
                    <span className="font-bold">{project.projectType}</span>
                  </div>
                  {project.surfaces && (
                    <>
                      <div className="flex justify-between">
                        <span className="opacity-90">Surface totale</span>
                        <span className="font-bold">{project.surfaces.totale} m²</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="opacity-90">Surface utilisable</span>
                        <span className="font-bold">{project.surfaces.utilisable} m²</span>
                      </div>
                    </>
                  )}
                  {project.altitude && (
                    <div className="flex justify-between">
                      <span className="opacity-90">Altitude</span>
                      <span className="font-bold">{project.altitude} m</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="opacity-90">Statut</span>
                    <span className="font-bold">{project.status}</span>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <AnimatedSection animation="fade-up">
        <div className="bg-[#c4d600] py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              Vous avez un projet similaire ?
            </h2>
            <p className="text-lg text-gray-700 mb-8">
              Contactez notre équipe pour discuter de votre projet de construction.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-gray-800 hover:bg-gray-900 text-white"
              >
                <Link to="/contact">
                  Demander un devis
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-gray-800 text-gray-800 hover:bg-gray-800 hover:text-white"
              >
                <Link to="/projet-construction">
                  En savoir plus
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </AnimatedSection>
    </div>
  );
};

export default ConstructionProjectDetailPage;
